import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';

interface CommandParameterDialogProps {
  open: boolean;
  onClose: () => void;
  /** Command to attach the parameter to. Omitted while drafting on the create page. */
  commandId?: number | string;
  parameter?: any;
  mode: 'create' | 'edit';
  /** Pre-selected order for a new parameter (the create page counts drafts). */
  defaultOrder?: number;
  /**
   * Draft mode: when set, the dialog hands the values to the caller instead of
   * writing them to the API. Used by the create page, where no command id exists
   * yet (there is no nested-create support on `POST /v3/api/commands`).
   */
  onSubmit?: (values: Record<string, any>) => void;
}

const buildFormData = (parameter: any, commandId?: number | string, defaultOrder?: number) => ({
  command_id: commandId,
  name: parameter?.name || '',
  display_name: parameter?.display_name || '',
  description: parameter?.description || '',
  data_type: parameter?.data_type || 'string',
  ui_component: parameter?.ui_component || 'text',
  is_required: parameter?.is_required || false,
  order: parameter?.order ?? defaultOrder ?? 0,
  default_value: parameter?.default_value || '',
  validation_rules: parameter?.validation_rules || {},
  ui_options: parameter?.ui_options || [],
});

export const CommandParameterDialog: React.FC<CommandParameterDialogProps> = ({
  open,
  onClose,
  commandId,
  parameter,
  mode,
  defaultOrder,
  onSubmit,
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();

  const [formData, setFormData] = useState(() => buildFormData(parameter, commandId, defaultOrder));

  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = useCallback(() => {
    setFormData(buildFormData(null, commandId, defaultOrder));
    setErrors({});
  }, [commandId, defaultOrder]);

  // Reload the form every time the dialog opens, so "Add parameter" never shows
  // the values of the parameter that was edited (or added) before it.
  useEffect(() => {
    if (!open) return;

    setErrors({});
    setFormData(buildFormData(mode === 'edit' ? parameter : null, commandId, defaultOrder));
  }, [open, mode, parameter, commandId, defaultOrder]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // Check if current UI component needs options
  const needsOptions = () => {
    return ['select', 'multiselect', 'radio', 'checkbox'].includes(formData.ui_component);
  };

  // Handle adding a new option
  const handleAddOption = () => {
    const newOption = {
      value: '',
      label: '',
      description: '',
    };
    setFormData((prev) => ({
      ...prev,
      ui_options: [...(prev.ui_options || []), newOption],
    }));
  };

  // Handle updating an option
  const handleUpdateOption = (index: number, field: string, value: any) => {
    const updatedOptions = [...(formData.ui_options || [])];
    updatedOptions[index] = { ...updatedOptions[index], [field]: value };
    setFormData((prev) => ({ ...prev, ui_options: updatedOptions }));
  };

  // Handle removing an option
  const handleRemoveOption = (index: number) => {
    const updatedOptions = (formData.ui_options || []).filter((_: any, i: number) => i !== index);
    setFormData((prev) => ({ ...prev, ui_options: updatedOptions }));
  };

  // Handle updating validation rules
  const handleValidationRuleChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      validation_rules: {
        ...prev.validation_rules,
        [field]: value === '' ? undefined : value,
      },
    }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.display_name) newErrors.display_name = 'Display name is required';
    if (!formData.data_type) newErrors.data_type = 'Data type is required';
    if (!formData.ui_component) newErrors.ui_component = 'UI component is required';
    if (formData.order === null || formData.order === undefined) {
      newErrors.order = 'Order is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    // Draft mode: the create page keeps the parameter in local state and saves it
    // once the command itself exists.
    if (onSubmit) {
      onSubmit({ ...formData });
      resetForm();
      onClose();
      return;
    }

    const path = `commands/${formData.command_id}/parameters`;
    try {
      if (mode === 'create') {
        await dataProvider.create(path, {
          data: formData,
        });
        notify('Parameter created successfully', { type: 'success' });
      } else {
        await dataProvider.update(path, {
          id: parameter.id,
          data: formData,
          previousData: parameter,
        });
        notify('Parameter updated successfully', { type: 'success' });
      }
      refresh();
      resetForm();
      onClose();
    } catch (error: any) {
      notify(error.message || 'Error saving parameter', { type: 'error' });
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{mode === 'create' ? 'Add Parameter' : 'Edit Parameter'}</DialogTitle>
      <DialogContent>
        <TextField
          margin="dense"
          label="Name *"
          fullWidth
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
          sx={{ mt: 2 }}
        />
        <TextField
          margin="dense"
          label="Display Name *"
          fullWidth
          value={formData.display_name}
          onChange={(e) => handleChange('display_name', e.target.value)}
          error={!!errors.display_name}
          helperText={errors.display_name}
        />
        <TextField
          margin="dense"
          label="Description"
          fullWidth
          multiline
          rows={3}
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
        />
        <FormControl fullWidth margin="dense" error={!!errors.data_type}>
          <InputLabel>Data Type *</InputLabel>
          <Select
            value={formData.data_type}
            label="Data Type *"
            onChange={(e) => handleChange('data_type', e.target.value)}
          >
            <MenuItem value="string">String</MenuItem>
            <MenuItem value="integer">Integer</MenuItem>
            <MenuItem value="boolean">Boolean</MenuItem>
            <MenuItem value="array">Array</MenuItem>
            <MenuItem value="object">Object</MenuItem>
          </Select>
          {errors.data_type && <FormHelperText>{errors.data_type}</FormHelperText>}
        </FormControl>
        <FormControl fullWidth margin="dense" error={!!errors.ui_component}>
          <InputLabel>UI Component *</InputLabel>
          <Select
            value={formData.ui_component}
            label="UI Component *"
            onChange={(e) => handleChange('ui_component', e.target.value)}
          >
            <MenuItem value="text">Text Input</MenuItem>
            <MenuItem value="textarea">Text Area</MenuItem>
            <MenuItem value="number">Number Input</MenuItem>
            <MenuItem value="select">Select (Single)</MenuItem>
            <MenuItem value="multiselect">Multi-Select</MenuItem>
            <MenuItem value="checkbox">Checkbox</MenuItem>
            <MenuItem value="radio">Radio Buttons</MenuItem>
          </Select>
          {errors.ui_component && <FormHelperText>{errors.ui_component}</FormHelperText>}
        </FormControl>

        {/* UI Options Editor - Show only for select, multiselect, radio, checkbox */}
        {needsOptions() && (
          <Box sx={{ mt: 2, mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">Options</Typography>
              <Button startIcon={<AddIcon />} size="small" onClick={handleAddOption}>
                Add Option
              </Button>
            </Box>
            {formData.ui_options && formData.ui_options.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No options defined. Click "Add Option" to create choices for this field.
              </Typography>
            )}
            {formData.ui_options?.map((option: any, index: number) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: 'grey.50',
                  borderRadius: 1,
                  position: 'relative',
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleRemoveOption(index)}
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                <TextField
                  margin="dense"
                  label="Value *"
                  fullWidth
                  value={option.value || ''}
                  onChange={(e) => handleUpdateOption(index, 'value', e.target.value)}
                  helperText="The actual value sent to the API"
                  size="small"
                />
                <TextField
                  margin="dense"
                  label="Label *"
                  fullWidth
                  value={option.label || ''}
                  onChange={(e) => handleUpdateOption(index, 'label', e.target.value)}
                  helperText="The text shown to the user"
                  size="small"
                />
                <TextField
                  margin="dense"
                  label="Description (optional)"
                  fullWidth
                  value={option.description || ''}
                  onChange={(e) => handleUpdateOption(index, 'description', e.target.value)}
                  helperText="Optional tooltip or help text"
                  size="small"
                />
              </Box>
            ))}
          </Box>
        )}

        <FormControlLabel
          control={
            <Checkbox checked={formData.is_required} onChange={(e) => handleChange('is_required', e.target.checked)} />
          }
          label="Required"
        />
        <TextField
          margin="dense"
          label="Order *"
          fullWidth
          type="number"
          value={formData.order}
          onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
          error={!!errors.order}
          helperText={errors.order}
        />
        <TextField
          margin="dense"
          label="Default Value"
          fullWidth
          value={formData.default_value}
          onChange={(e) => handleChange('default_value', e.target.value)}
        />

        {/* Validation Rules Editor */}
        <Box sx={{ mt: 2, mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Validation Rules
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {(formData.data_type === 'string' || formData.data_type === 'integer') && (
              <>
                <TextField
                  margin="dense"
                  label={formData.data_type === 'string' ? 'Min Length' : 'Min Value'}
                  type="number"
                  value={formData.validation_rules?.min || ''}
                  onChange={(e) => handleValidationRuleChange('min', e.target.value ? parseInt(e.target.value) : '')}
                  size="small"
                />
                <TextField
                  margin="dense"
                  label={formData.data_type === 'string' ? 'Max Length' : 'Max Value'}
                  type="number"
                  value={formData.validation_rules?.max || ''}
                  onChange={(e) => handleValidationRuleChange('max', e.target.value ? parseInt(e.target.value) : '')}
                  size="small"
                />
              </>
            )}
            {formData.data_type === 'array' && (
              <>
                <TextField
                  margin="dense"
                  label="Min Items"
                  type="number"
                  value={formData.validation_rules?.min_items || ''}
                  onChange={(e) =>
                    handleValidationRuleChange('min_items', e.target.value ? parseInt(e.target.value) : '')
                  }
                  size="small"
                />
                <TextField
                  margin="dense"
                  label="Max Items"
                  type="number"
                  value={formData.validation_rules?.max_items || ''}
                  onChange={(e) =>
                    handleValidationRuleChange('max_items', e.target.value ? parseInt(e.target.value) : '')
                  }
                  size="small"
                />
              </>
            )}
          </Box>
          {formData.data_type === 'string' && (
            <TextField
              margin="dense"
              label="Regex Pattern"
              fullWidth
              value={formData.validation_rules?.regex || ''}
              onChange={(e) => handleValidationRuleChange('regex', e.target.value)}
              helperText="Regular expression pattern for validation"
              size="small"
              sx={{ mt: 1 }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
